import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Locale } from 'date-fns';
import '@/locales'; // Initialize i18next
import { useUserLanguage, type SupportedLanguage } from '@/hooks/useUserLanguage';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { useAuth } from '@/hooks/useAuth';

interface LocaleContextValue {
  // Language
  language: SupportedLanguage;
  setLanguage: (lang: SupportedLanguage) => Promise<void>;
  t: (key: string, options?: Record<string, unknown>) => string;
  
  // Timezone
  userTimezone: string;
  officeTimezone: string;
  setTimezone: (tz: string | null) => Promise<void>;
  formatInUserTz: (date: Date | string | number, format?: string) => string;
  formatInOfficeTz: (date: Date | string | number, format?: string) => string;
  getCurrentUserTime: (format?: string) => string;
  getCurrentOfficeTime: () => string;
  
  // Date formats
  dateFormats: {
    short: string;
    long: string;
    dateTime: string;
    dateTimeShort: string;
    time: string;
    timeWithSeconds: string;
    inputDate: string;
  };
  dateLocale: Locale;
  use24HourTime: boolean;
  
  // Loading state
  isLoading: boolean;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

interface LocaleProviderProps {
  children: ReactNode;
}

export const LocaleProvider: React.FC<LocaleProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [isReady, setIsReady] = useState(false);

  // Only load hooks when user is authenticated
  const languageHook = useUserLanguage();
  const timezoneHook = useUserTimezone();

  // Wait for initial load
  useEffect(() => {
    if (!languageHook.isLoading && !timezoneHook.isLoading) {
      setIsReady(true);
    }
  }, [languageHook.isLoading, timezoneHook.isLoading]);

  const value: LocaleContextValue = {
    // Language
    language: languageHook.language,
    setLanguage: languageHook.setLanguage,
    t,
    
    // Timezone
    userTimezone: timezoneHook.userTimezone,
    officeTimezone: timezoneHook.officeTimezone,
    setTimezone: timezoneHook.setTimezone,
    formatInUserTz: timezoneHook.formatInUserTz,
    formatInOfficeTz: timezoneHook.formatInOfficeTz,
    getCurrentUserTime: timezoneHook.getCurrentUserTime,
    getCurrentOfficeTime: timezoneHook.getCurrentOfficeTime,
    
    // Date formats
    dateFormats: languageHook.dateFormats,
    dateLocale: languageHook.dateLocale,
    use24HourTime: languageHook.use24HourTime,
    
    // Loading state
    isLoading: languageHook.isLoading || timezoneHook.isLoading,
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextValue => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

// Export a hook that works outside of LocaleProvider (for non-authenticated routes)
export const useLocaleOptional = (): LocaleContextValue | null => {
  return useContext(LocaleContext);
};
